import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { DatabaseEnum, DatabaseManager } from "@robotical/analytics-gatherer";
import {
  HSTicket,
  HubspotTicketStatus,
  RegisterReportsGetResponse as LegacyReports,
} from "@robotical/analytics-gatherer/dist/types";
import ServiceProgramDatabase, {
  getHSTicketsFromReports,
} from "@robotical/analytics-gatherer/dist/database/ServiceProgramDatabase";
import {
  RegisterReportsGetResponse,
  ZohoTicket,
  ZohoTicketStatus,
} from "@robotical/appv2-warranty-service-lib/dist/types/serviceProgramDatabase";
import WarrantyServiceFacade from "@robotical/appv2-warranty-service-lib/dist/facade/WarrantyServiceFacade";
import { toast } from "react-toastify";
import serviceProgramFirebaseConfig from "../database/config";
import { RegisteredUser } from "../types/generic";

export type ServiceTicket = {
  ticket: HSTicket | ZohoTicket;
  id: string;
  serialNumber: string;
  robotName: string;
};
type TicketContextValue = {
  tickets: ServiceTicket[];
  loading: boolean;
  failedRobots: string[];
  pending: string[];
  reload: () => void;
  updateStatus: (
    entry: ServiceTicket,
    status: HubspotTicketStatus | ZohoTicketStatus,
  ) => Promise<void>;
};
const TicketContext = createContext<TicketContextValue>({
  tickets: [],
  loading: true,
  failedRobots: [],
  pending: [],
  reload: () => {},
  updateStatus: async () => {},
});

// Existing ticket API calls are shared by the robot summary and ticket view.
// Backend report and status types remain unchanged, including legacy HubSpot tickets.
export function TicketProvider({
  user,
  refreshKey,
  children,
}: {
  user: RegisteredUser | null;
  refreshKey: boolean;
  children: React.ReactNode;
}) {
  const [tickets, setTickets] = useState<ServiceTicket[]>([]);
  const [loading, setLoading] = useState(true);
  const [failedRobots, setFailedRobots] = useState<string[]>([]);
  const [pending, setPending] = useState<string[]>([]);
  const pendingRef = useRef(new Set<string>());
  const [version, setVersion] = useState(0);
  const serialNumbers = user?.serialNumbers;
  useEffect(() => {
    let active = true;
    setLoading(true);
    setFailedRobots([]);
    setTickets([]);
    const load = async () => {
      const failures: string[] = [];
      const groups = await Promise.all(
        Object.entries(serialNumbers || {}).map(
          async ([serialNumber, robot]) => {
            try {
              const reports = robot.reports;
              if (!reports || Object.keys(reports).length === 0) return [];
              const legacy =
                "hubspotTicketId" in reports ||
                Object.values(reports).some(
                  (report) =>
                    report !== null &&
                    typeof report === "object" &&
                    "hubspotTicketId" in report,
                );
              let fetched: (HSTicket | ZohoTicket)[];
              if (legacy) {
                const dbManager = DatabaseManager.getInstance();
                await dbManager.initializeOrGetDatabase(
                  DatabaseEnum.SERVICE_PROGRAM,
                  serviceProgramFirebaseConfig,
                  DatabaseEnum.SERVICE_PROGRAM,
                );
                fetched = (
                  await getHSTicketsFromReports(reports as LegacyReports)
                ).filter(Boolean);
              } else {
                const response = await WarrantyServiceFacade.getZohoReports(
                  reports as RegisterReportsGetResponse,
                );
                if (response === null)
                  throw new Error("Tickets could not be loaded");
                fetched = response || [];
              }
              return fetched.filter(Boolean).map((ticket) => ({
                ticket,
                id:
                  "hubspotTicketId" in ticket
                    ? ticket.hubspotTicketId
                    : ticket.id,
                serialNumber,
                robotName: robot.name || "Marty",
              }));
            } catch (error) {
              console.error("Error loading tickets:", error);
              failures.push(serialNumber);
              return [];
            }
          },
        ),
      );
      if (active) {
        setTickets(groups.flat());
        setFailedRobots(failures);
        setLoading(false);
      }
    };
    load();
    return () => {
      active = false;
    };
  }, [serialNumbers, user?.email, refreshKey, version]);
  const updateStatus = useCallback(
    async (
      entry: ServiceTicket,
      status: HubspotTicketStatus | ZohoTicketStatus,
    ) => {
      const key = `${entry.serialNumber}:${entry.id}`;
      if (pendingRef.current.has(key)) return;
      pendingRef.current.add(key);
      setPending(Array.from(pendingRef.current));
      try {
        let didUpdate: boolean;
        if ("hubspotTicketId" in entry.ticket) {
          const dbManager = DatabaseManager.getInstance();
          const db = (await dbManager.initializeOrGetDatabase(
            DatabaseEnum.SERVICE_PROGRAM,
            serviceProgramFirebaseConfig,
            DatabaseEnum.SERVICE_PROGRAM,
          )) as ServiceProgramDatabase;
          didUpdate = Boolean(
            await db.hubspotTicketManager.updateTicketStatus(
              entry.id,
              status as HubspotTicketStatus,
            ),
          );
        } else {
          didUpdate = Boolean(
            await WarrantyServiceFacade.updateZohoTicketStatus(
              entry.id,
              status as ZohoTicketStatus,
            ),
          );
        }
        if (didUpdate) {
          setTickets((current) =>
            current.map((item) =>
              item.id === entry.id && item.serialNumber === entry.serialNumber
                ? {
                    ...item,
                    ticket: { ...item.ticket, status } as HSTicket | ZohoTicket,
                  }
                : item,
            ),
          );
          toast.success("Ticket updated successfully", { autoClose: 2000 });
        } else {
          toast.error("Failed to update ticket", { autoClose: 2000 });
        }
      } catch (error) {
        console.error("Error updating ticket status:", error);
        toast.error("Failed to update ticket", { autoClose: 2000 });
      } finally {
        pendingRef.current.delete(key);
        setPending(Array.from(pendingRef.current));
      }
    },
    [],
  );
  return (
    <TicketContext.Provider
      value={{
        tickets,
        loading,
        failedRobots,
        pending,
        reload: () => setVersion((value) => value + 1),
        updateStatus,
      }}
    >
      {children}
    </TicketContext.Provider>
  );
}
export function useTickets() {
  return useContext(TicketContext);
}
